import { useState, type CSSProperties, type SyntheticEvent } from 'react';
import { ResponsiveImage } from './ResponsiveImage';

/**
 * Card image component.
 *
 * Per frontend.md §15.3:
 *  - Uses the CloudFront `image_url` from the scraper contract.
 *  - `loading="lazy"` + explicit width/height to avoid layout shift.
 *  - On error, falls back to a gray card-back placeholder SVG rendered
 *    inline so we never make a second network request.
 *
 * Only `image_url` is read. `image_source_url` is debug-only per the
 * scraper handoff (data-model.md — Field caveats).
 */

export interface CardImageProps {
  src: string | null | undefined;
  /** Thumbnail variant URL (245w). Falls back to src when absent. */
  srcThumb?: string | null;
  /** 2× variant URL (980w). Omitted from srcSet when absent. */
  src2x?: string | null;
  /** `sizes` passed to ResponsiveImage. Defaults to card-grid context. */
  sizes?: string;
  alt: string;
  width?: number;
  height?: number;
  style?: CSSProperties;
  className?: string;
}

// Gray 600x836 card back. Dimensions match the canonical webp aspect
// ratio so the element doesn't jump when the fallback swaps in.
function CardBackFallback(props: {
  width: number;
  height: number;
  style?: CSSProperties;
}): JSX.Element {
  const style: CSSProperties = props.style ?? {};
  return (
    <svg
      role="img"
      aria-label="Card image unavailable"
      width={props.width}
      height={props.height}
      viewBox="0 0 600 836"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="card-image-fallback"
      style={style}
    >
      <rect x={0} y={0} width={600} height={836} rx={36} ry={36} fill="#cbd5e1" />
      <rect
        x={40}
        y={40}
        width={520}
        height={756}
        rx={24}
        ry={24}
        fill="none"
        stroke="#94a3b8"
        strokeWidth={6}
      />
      <text
        x={300}
        y={440}
        fontFamily="system-ui, sans-serif"
        fontSize={56}
        fontWeight={600}
        textAnchor="middle"
        fill="#475569"
      >
        Pull It Packs
      </text>
    </svg>
  );
}

export function CardImage(props: CardImageProps): JSX.Element {
  const width = props.width ?? 300;
  const height = props.height ?? 418;
  const [errored, setErrored] = useState<boolean>(false);

  const handleError = (_event: SyntheticEvent<HTMLImageElement>): void => {
    setErrored(true);
  };

  if (errored || !props.src) {
    return <CardBackFallback width={width} height={height} style={props.style ?? {}} />;
  }

  return (
    <ResponsiveImage
      urls={{
        thumb: props.srcThumb,
        default: props.src,
        _2x: props.src2x,
      }}
      sizes={props.sizes ?? '(max-width: 640px) 140px, 240px'}
      alt={props.alt}
      width={width}
      height={height}
      onError={handleError}
      data-testid="card-image"
      className={props.className}
      style={props.style ?? {}}
    />
  );
}
